/**
 * Starred chart providers — the user's favourites, kept on the device so they
 * survive reloads. The chart-provider picker pins and highlights these. Mirrors
 * the satellite store's localStorage pattern; just a set of provider ids.
 */
import { create } from 'zustand';

const LS = 'acp.chartFavourites.v1';

function load(): string[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS) : null;
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function save(ids: string[]): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS, JSON.stringify(ids));
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

interface ChartFavouritesState {
  /** Provider ids the user has starred. */
  favIds: string[];
  isFavourite: (id: string) => boolean;
  toggleFavourite: (id: string) => void;
}

export const useChartFavouritesStore = create<ChartFavouritesState>((set, get) => ({
  favIds: load(),
  isFavourite: (id) => get().favIds.includes(id),
  toggleFavourite: (id) =>
    set((s) => {
      const favIds = s.favIds.includes(id)
        ? s.favIds.filter((x) => x !== id)
        : [...s.favIds, id];
      save(favIds);
      return { favIds };
    }),
}));
